import * as React from 'react';
import { Card } from './card';
import { cn } from './utils';

/**
 * FormSection — labeled group of fields/rows inside a form or summary page.
 * Renders a small heading above a Card so we don't keep re-typing the
 * `<section><h3 …><Card …>` triplet inline. Use `actions` for inline
 * controls next to the title (e.g. "Modifica" link in a summary).
 *
 * For section heads above non-Card content (tables, custom layouts) prefer
 * `SectionHeader` directly.
 */

export interface FormSectionProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  cardClassName?: string;
}

export function FormSection({
  title,
  description,
  actions,
  cardClassName,
  className,
  children,
  ...props
}: FormSectionProps): JSX.Element {
  return (
    <section className={cn('grid gap-2', className)} {...props}>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      <Card className={cn('p-4', cardClassName)}>{children}</Card>
    </section>
  );
}
